// Which diagram is being edited, and the live handle on its editor (TODO §21/§6, §8.2).
//
// This context is what enforces **§5.1, the one-instance rule**, structurally rather than by
// convention: `editingDocId` holds at most one id, so at most one `<Excalidraw>` can be
// mounted. Every other diagram on screen — the other window, the nav thumbnails, the print
// layer, presentation, the off-screen capturer — renders the cached SVG instead. That single
// decision is what keeps memory, export, print, thumbnails and presentation from each
// needing their own answer.
//
// It is provided ONLY by `DeckEditor`. The print/presentation overlays and the off-screen
// thumbnail capturer render `<Slide>` outside it, so they get the inert default below and
// **cannot** mount an editor even by mistake — the same trick `ThumbnailScheduleProvider`
// uses to keep those copies from scheduling grid captures.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

// Type-only import: erased at build time, so this file does NOT pull the Excalidraw chunk
// into the main bundle (§10.3). Verify from the built asset list, not from intent (§16.11).

export interface DiagramEditingValue {
  // §6.2 The selected diagram window. Selecting is not editing: it makes the rail's Diagram
  // button meaningful and shows the "double-click to edit" affordance, and mounts nothing.
  //
  // Scoped to diagrams rather than being the generic selected-content-window state §8.2 and
  // §15.7.2 both want. That generalisation touches every content kind and every window, and
  // doing it here would mean rewriting `Slide`/`ContentWindow` for a feature neither of the
  // other two is ready to use; when §15 lands, this is the thing it widens.
  selectedDocId: string | null;
  select: (docId: string | null) => void;
  // §6.3 The diagram whose editor is mounted. At most one, ever.
  editingDocId: string | null;
  beginEdit: (docId: string) => void;
  endEdit: () => void;
  // §8.2 The mounted editor's imperative handle, so the right panel can read the live
  // selection and write properties back. Null whenever nothing is being edited.
  api: ExcalidrawImperativeAPI | null;
  setApi: (api: ExcalidrawImperativeAPI | null) => void;
  // §8.2 Scene changes reach the right panel through the module-level emitter below
  // (`subscribeScene` / `sceneVersion`), NOT through this context — see the note on it. The
  // only thing on the context is the notifier the mounted editor calls.
  noteSceneChange: () => void;
  // §3.4 The measured LOGICAL size of each diagram's content window, so the right panel's
  // "Fit frame to window" has a target. Written through a ref map rather than state: a
  // window's size changes on every frame of a split drag, and a state write per frame would
  // re-render the whole deck to feed a number that is only ever read on a button click.
  reportWindowSize: (
    docId: string,
    size: { width: number; height: number },
  ) => void;
  getWindowSize: (docId: string) => { width: number; height: number } | null;
}

const INERT: DiagramEditingValue = {
  selectedDocId: null,
  select: () => {},
  editingDocId: null,
  beginEdit: () => {},
  endEdit: () => {},
  api: null,
  setApi: () => {},
  noteSceneChange: () => {},
  reportWindowSize: () => {},
  getWindowSize: () => null,
};

const DiagramEditingContext = createContext<DiagramEditingValue>(INERT);

// === The scene-change signal (§8.2) — deliberately NOT React state ===
//
// This started life as a `sceneTick` counter in the provider's state, and that was an
// **infinite render loop**, reproducible by opening any diagram:
//
//   Excalidraw `onChange` → `setSceneTick` → a new context value → every consumer re-renders
//   → `DiagramBlock` re-renders → `DiagramEditor` hands `<Excalidraw>` freshly-allocated
//   `onChange` / `UIOptions` / `excalidrawAPI` props → the memo does not hold, it updates →
//   `onChange` fires again → "Maximum update depth exceeded".
//
// The prop memoisation in `DiagramEditor` closes the second half of that cycle, but the first
// half is wrong on its own terms: `onChange` fires on pointer moves and selection changes, so
// routing it through context re-rendered the WHOLE deck — every slide, every window — at up
// to 60fps to feed a number that exactly one component reads. An external store re-renders
// only the subscriber. This is the same shape as `diagramSvg.ts`'s cache and for the same
// reason.
//
// Module-level rather than per-provider because there is only ever one editing session in a
// page (§5.1), and because the getter and subscriber must have stable identities — which is
// precisely what putting them on the context could not guarantee.
let sceneSeq = 0;
const sceneListeners = new Set<() => void>();

export function subscribeScene(listener: () => void): () => void {
  sceneListeners.add(listener);
  return () => sceneListeners.delete(listener);
}

export function sceneVersion(): number {
  return sceneSeq;
}

export function DiagramEditingProvider({
  children,
  onEnterEdit,
  onExitEdit,
}: {
  children: ReactNode;
  // §6.3/§7.1 The left panel switches to Elements on entering edit and back to Slides on
  // leaving. Passed in rather than owned here so this file stays about diagram state and
  // knows nothing about the nav panel.
  onEnterEdit?: () => void;
  onExitEdit?: () => void;
}) {
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [api, setApi] = useState<ExcalidrawImperativeAPI | null>(null);
  // Latest callbacks without re-creating the memoised value (and so remounting the editor)
  // on every DeckEditor render.
  const enterRef = useRef(onEnterEdit);
  enterRef.current = onEnterEdit;
  const exitRef = useRef(onExitEdit);
  exitRef.current = onExitEdit;

  const beginEdit = useCallback((docId: string) => {
    setSelectedDocId(docId);
    setEditingDocId(docId);
    enterRef.current?.();
  }, []);

  const endEdit = useCallback(() => {
    setEditingDocId(null);
    setApi(null);
    exitRef.current?.();
  }, []);

  // Notifies the external store above. No React state is touched, so this does not re-render
  // anything except the components that actually subscribed.
  const noteSceneChange = useCallback(() => {
    sceneSeq += 1;
    for (const listener of sceneListeners) listener();
  }, []);

  const windowSizes = useRef(
    new Map<string, { width: number; height: number }>(),
  );
  const reportWindowSize = useCallback(
    (docId: string, size: { width: number; height: number }) => {
      if (size.width > 0 && size.height > 0)
        windowSizes.current.set(docId, size);
    },
    [],
  );
  const getWindowSize = useCallback(
    (docId: string) => windowSizes.current.get(docId) ?? null,
    [],
  );

  const value = useMemo<DiagramEditingValue>(
    () => ({
      selectedDocId,
      select: setSelectedDocId,
      editingDocId,
      beginEdit,
      endEdit,
      api,
      setApi,
      noteSceneChange,
      reportWindowSize,
      getWindowSize,
    }),
    [
      selectedDocId,
      editingDocId,
      beginEdit,
      endEdit,
      api,
      noteSceneChange,
      reportWindowSize,
      getWindowSize,
    ],
  );

  return (
    <DiagramEditingContext.Provider value={value}>
      {children}
    </DiagramEditingContext.Provider>
  );
}

export function useDiagramEditing(): DiagramEditingValue {
  return useContext(DiagramEditingContext);
}
